/**
 * Response post-processing for `useChat`, kept free of React state so it can
 * be characterised in isolation: turning a JSON chat response into a reply
 * bubble, and the side effects that follow (thread issued, outcomes,
 * escalation, analytics, thread refresh, redirect).
 */
import type {
  ChatAction,
  ChatWidgetProps,
  Message,
  MessageResult,
  MessageType,
  RichContent,
  SessionConfig,
  SourceCitation,
  ToolCall,
} from '../types';
import { MESSAGE_TYPES } from '../constants/messageTypes';
import { getSessionId } from '../utils/session';
import { getNextMessageId } from '../utils/chatHelpers';
import { storeThreadToken } from '../utils/threadCapability';
import { navigateToUrl } from '../utils/navigation';
import { detectChatAnalyticsIntent } from '../utils/analyticsIntent';
import type { WidgetStrings } from '../utils/i18n';
import type { AttachmentPayload } from './chatRequest';

export interface ParseChatReplyOptions {
  parseResponse?: ChatWidgetProps['parseResponse'];
  enableRichMessages: boolean;
  enableSourceCitations: boolean;
  enableToolVisualization: boolean;
  strings: WidgetStrings;
}

export interface ParsedChatReply {
  reply: Message;
  messageType: MessageType;
  richContent: RichContent | undefined;
  /** Whatever the host's `parseResponse` returned (or null). */
  parsed: unknown;
}

/** Builds the assistant bubble from a JSON chat response. */
export function parseChatReply(
  data: Record<string, unknown>,
  {
    parseResponse,
    enableRichMessages,
    enableSourceCitations,
    enableToolVisualization,
    strings,
  }: ParseChatReplyOptions
): ParsedChatReply {
  const parsed = typeof parseResponse === 'function' ? parseResponse(data) : null;

  let messageType: MessageType = MESSAGE_TYPES.TEXT;
  let richContent: RichContent | undefined = undefined;

  if (enableRichMessages && data.type) {
    messageType = data.type as MessageType;
    richContent = (data.content || data.richContent) as RichContent;
  }

  if (enableRichMessages && messageType === MESSAGE_TYPES.PRODUCT_CARDS && !richContent) {
    const products = data.products || data.recommendations;
    if (Array.isArray(products)) {
      richContent = { products: products as RichContent['products'] };
    }
  }

  // Server contract: JSON chat replies carry the stored AI message id as a
  // top-level `messageId`; feedback and other server calls must use it.
  const serverId =
    typeof data.messageId === 'string' && data.messageId ? data.messageId : undefined;

  const reply: Message = {
    id: `${getNextMessageId()}-ai`,
    ...(serverId ? { serverId } : {}),
    sender: 'ai',
    text:
      (parsed as { text?: string } | null)?.text ??
      (data as { reply?: string }).reply ??
      (data as { message?: string }).message ??
      (data as { text?: string }).text ??
      strings.replyReceivedFallback,
    timestamp: new Date().toISOString(),
    status: 'delivered',
    type: messageType,
    richContent,
    suggestions:
      (parsed as { suggestions?: string[] } | null)?.suggestions ??
      (data as { suggestions?: string[] }).suggestions ??
      [],
    sources: enableSourceCitations
      ? ((data.sources || data.citations) as SourceCitation[] | undefined)
      : undefined,
    toolCalls: enableToolVisualization ? (data.toolCalls as ToolCall[] | undefined) : undefined,
  };

  return { reply, messageType, richContent, parsed };
}

export interface ChatResponseEffectDeps {
  threadId: string | null;
  setThreadId: (id: string | null) => void;
  onThreadIssued?: (issued: { threadId?: string | null; threadToken?: string | null }) => void;
  sessionConfig?: SessionConfig;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  trackOutcome?: (outcome: unknown) => void;
  buildAnalyticsEnvelope: (extra?: Record<string, unknown>) => Record<string, unknown>;
  dispatch: (action: ChatAction) => void;
  onMessageCreate?: ChatWidgetProps['onMessageCreate'];
  fetchThread: () => Promise<void>;
}

export interface ChatResponseEffectInput {
  data: Record<string, unknown>;
  parsed: ParsedChatReply;
  localMsg: Message;
  attachments: AttachmentPayload[];
  requestMeta?: Record<string, unknown>;
}

function extractOutcomes(data: Record<string, unknown>): unknown {
  return (
    data.outcomes ??
    data.outcome ??
    data.agenticOutcomes ??
    data.agentic_outcomes ??
    data.agenticOutcome ??
    data.agentic_outcome
  );
}

/**
 * Applies everything a successful JSON reply triggers, in the order the widget
 * has always used. Returns the `MessageResult` handed back to callers.
 */
export async function applyChatResponseEffects(
  {
    data,
    parsed: { reply, messageType, richContent, parsed },
    localMsg,
    attachments: atts,
    requestMeta,
  }: ChatResponseEffectInput,
  deps: ChatResponseEffectDeps
): Promise<MessageResult> {
  const {
    threadId,
    setThreadId,
    onThreadIssued,
    sessionConfig,
    trackAnalytics,
    trackOutcome,
    buildAnalyticsEnvelope,
    dispatch,
    onMessageCreate,
    fetchThread,
  } = deps;

  if (messageType === MESSAGE_TYPES.PRODUCT_CARDS && richContent?.products?.length) {
    trackAnalytics('product_recommendations', {
      ...buildAnalyticsEnvelope(),
      count: richContent.products.length,
      ids: richContent.products.map(p => p?.id).filter(Boolean),
    });
  }

  const newThreadId =
    (parsed as { threadId?: string } | null)?.threadId ??
    (data as { threadId?: string }).threadId ??
    (data as { thread?: { id?: string } }).thread?.id ??
    null;
  if (newThreadId && newThreadId !== threadId) setThreadId(newThreadId);
  const effectiveThreadId = newThreadId || threadId || undefined;
  // Persist the per-thread capability token from chat responses; thread
  // reads must echo it (x-widget-thread-token) once the server enforces
  // WIDGET_THREAD_CAP_ENFORCE.
  const issuedToken = data.threadToken;
  if (typeof issuedToken === 'string' && issuedToken) {
    if (onThreadIssued) {
      onThreadIssued({ threadId: effectiveThreadId, threadToken: issuedToken });
    } else {
      storeThreadToken(effectiveThreadId, issuedToken, sessionConfig);
    }
  }

  const rawOutcome = extractOutcomes(data);
  if (trackOutcome && rawOutcome !== undefined) {
    const attachThread = (value: unknown) => {
      const outcomeThreadId = newThreadId || threadId;
      if (typeof value === 'string') return { name: value, threadId: outcomeThreadId };
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const obj = value as Record<string, unknown>;
        return { ...obj, threadId: (obj.threadId as unknown) ?? outcomeThreadId };
      }
      return value;
    };
    if (Array.isArray(rawOutcome)) {
      trackOutcome(rawOutcome.map(attachThread));
    } else {
      trackOutcome(attachThread(rawOutcome));
    }
  }

  const escalated = Boolean((data as { escalated?: boolean }).escalated);
  if (escalated) {
    dispatch({ type: 'SET_ESCALATED', isEscalated: true });
  }

  const redirect =
    data.redirect && typeof data.redirect === 'object'
      ? (data.redirect as { url?: unknown; target?: unknown })
      : null;
  const redirectUrl = typeof redirect?.url === 'string' ? redirect.url : null;
  const redirectTarget = redirect?.target === '_blank' ? '_blank' : '_self';

  dispatch({ type: 'ADD_MESSAGE', message: reply });
  onMessageCreate?.(localMsg, reply);
  trackAnalytics(
    'message_sent',
    buildAnalyticsEnvelope({
      threadId: effectiveThreadId,
      messageId: localMsg.id,
      messageType: localMsg.type || MESSAGE_TYPES.TEXT,
      sender: localMsg.sender,
      characterCount: localMsg.text.length,
      intent: detectChatAnalyticsIntent(localMsg.text),
      hasAttachments: atts?.length > 0,
      attachmentCount: atts?.length || 0,
      silent: requestMeta?.silent === true,
    })
  );
  trackAnalytics(
    'message_received',
    buildAnalyticsEnvelope({
      threadId: effectiveThreadId,
      messageId: reply.id,
      messageType,
      sender: reply.sender,
      characterCount: reply.text.length,
      suggestionsCount: reply.suggestions?.length || 0,
      toolCalls: reply.toolCalls?.length || 0,
      citations: reply.sources?.length || 0,
      escalated,
      responseTime:
        typeof requestMeta?.analyticsRequestStartedAt === 'number'
          ? Date.now() - requestMeta.analyticsRequestStartedAt
          : undefined,
    })
  );
  if (escalated) {
    trackAnalytics(
      'handoff_request',
      buildAnalyticsEnvelope({
        threadId: effectiveThreadId,
        source: 'response',
      })
    );
  }
  try {
    await fetchThread();
  } catch {
    // Thread refresh after message send is best-effort; failure is non-critical
  }
  if (redirectUrl) {
    navigateToUrl(redirectUrl, redirectTarget);
  }
  return { request: localMsg, response: reply, raw: data };
}

export interface SendFailureDescription {
  /** HTTP status parsed out of the error message, when present. */
  status: number | null;
  /** Generic user-facing copy; diagnostic detail never reaches the transcript. */
  userMessage: string;
  /** Integrator hint for the console (empty when none applies). */
  hint: string;
}

/** True for fetch-level failures (no HTTP response), e.g. a dropped connection. */
export function isNetworkSendError(errorObj: Error): boolean {
  const message = errorObj.message.toLowerCase();
  return message.includes('failed to fetch') || message.includes('networkerror');
}

/** Classifies a send failure into user copy plus a console hint for integrators. */
export function describeSendFailure(
  errorObj: Error,
  strings: WidgetStrings
): SendFailureDescription {
  const errorMessage = errorObj.message.toLowerCase();
  const statusMatch = errorMessage.match(/\b(\d{3})\b/);
  const status = statusMatch && statusMatch[1] ? Number(statusMatch[1]) : null;
  const userMessage = isNetworkSendError(errorObj)
    ? strings.sendFailedNetwork
    : status === 429
      ? strings.sendFailedRateLimited
      : status === 500 || status === 502 || status === 503 || status === 504
        ? strings.sendFailedServer
        : errorMessage.includes('timed out') || errorMessage.includes('timeout')
          ? strings.sendFailedTimeout
          : strings.sendFailedGeneric;
  const hint =
    status === 401 || status === 403
      ? 'Authentication is required: add a valid widgetAuthToken or API key.'
      : status === 400 || status === 422
        ? 'Check the widget configuration (agent, organization, and message payload).'
        : '';
  return { status, userMessage, hint };
}

export interface SendFailureContext {
  text: string;
  attachmentCount: number;
  meta?: Record<string, unknown>;
  /** Id chosen for the local echo bubble; the failed bubble reuses it. */
  localMessageId: string;
  /** Whether the hook is still mounted (nothing may be dispatched otherwise). */
  mounted: boolean;
  /** Whether this send is still the newest one and may settle loading/isTyping. */
  isLatestSend: boolean;
  /** Failed because the browser is offline: queue the bubble for reconnect. */
  offline?: boolean;
}

export interface SendFailureDeps {
  strings: WidgetStrings;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  buildAnalyticsEnvelope: (extra?: Record<string, unknown>) => Record<string, unknown>;
  dispatch: (action: ChatAction) => void;
  onError?: ChatWidgetProps['onError'];
}

/**
 * Everything a non-abort send failure triggers: console diagnostics for the
 * integrator, `message_send_failed` analytics, the failed bubble (or the banner
 * for silent sends) and the host's `onError`.
 */
export function applySendFailureEffects(
  errorObj: Error,
  {
    text,
    attachmentCount,
    meta,
    localMessageId,
    mounted,
    isLatestSend,
    offline,
  }: SendFailureContext,
  { strings, trackAnalytics, buildAnalyticsEnvelope, dispatch, onError }: SendFailureDeps
): void {
  // End users only ever see generic copy; the diagnostic detail (auth,
  // configuration, server error bodies) goes to the console for integrators.
  const { status, userMessage: failureMessage, hint } = describeSendFailure(errorObj, strings);
  const userMessage = offline ? strings.sendQueuedOffline : failureMessage;
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(
      `[response-chat-widget] Message send failed${status ? ` (HTTP ${status})` : ''}: ${errorObj.message}${hint ? ` — ${hint}` : ''}`
    );
  }
  trackAnalytics(
    'message_send_failed',
    buildAnalyticsEnvelope({
      message: errorObj.message,
      status,
      characterCount: text.length,
      attachmentCount,
      ...(offline ? { offline: true } : {}),
    })
  );
  if (mounted) {
    if (meta?.silent === true) {
      // Nothing is on screen for a silent send, so the banner is the only place
      // the failure can surface.
      if (isLatestSend) dispatch({ type: 'SEND_ERROR', error: userMessage });
    } else {
      // The user bubble becomes the error surface (red outline, caption, Retry);
      // the draft is NOT restored so the text is never shown twice.
      dispatch({
        type: 'MARK_MESSAGE_FAILED',
        message: {
          id: localMessageId,
          sender: 'user',
          text,
          timestamp: new Date().toISOString(),
          status: 'failed',
          // Retry resends text only, so a message with attachments keeps the manual Retry
          // rather than silently going out without them.
          ...(offline && !attachmentCount ? { retryOnReconnect: true } : {}),
        },
        error: userMessage,
        meta,
      });
      if (isLatestSend) dispatch({ type: 'SEND_SUCCESS' });
    }
  }
  onError?.(errorObj);
}

export interface ChatAnalyticsContext {
  sessionConfig?: SessionConfig;
  threadId: string | null;
  channel: string;
  provider?: string;
  department: string;
  subject: string;
}

/** Common fields every chat analytics event carries; `extra` wins on collisions. */
export function buildChatAnalyticsEnvelope(
  { sessionConfig, threadId, channel, provider, department, subject }: ChatAnalyticsContext,
  extra: Record<string, unknown> = {}
) {
  const effectiveThreadId = (extra.threadId as string | null | undefined) || threadId || undefined;
  return {
    sessionId: getSessionId(sessionConfig) || undefined,
    threadId: effectiveThreadId,
    conversationId: effectiveThreadId,
    channel,
    provider,
    department,
    subject: subject || undefined,
    ...extra,
  };
}

/** `message_received` payload for a streamed reply (the JSON path emits its own in the effects above). */
export function streamedReplyAnalytics(
  response: Message,
  { text, threadId, elapsedMs }: { text: string; threadId?: string; elapsedMs: number }
) {
  return {
    threadId,
    messageId: response.id,
    messageType: response.type || MESSAGE_TYPES.TEXT,
    sender: response.sender,
    characterCount: response.text.length,
    intent: detectChatAnalyticsIntent(text),
    streamed: true,
    toolCalls: response.toolCalls?.length || 0,
    citations: response.sources?.length || 0,
    responseTime: elapsedMs,
  };
}
